(() => {
  'use strict';

  const REGISTRY_ID = 'BpcColorMediaRegistry-v1';
  const INITIAL_STYLE_ID = 'BpcColorGalleryInitialStyle-v1';
  const READY_FLAG = '__bpcColorGalleryResolverV1';

  if (window[READY_FLAG]) return;
  window[READY_FLAG] = true;

  const normalize = (value) => String(value || '').trim().toLowerCase();
  const uniqueStrings = (values) => [...new Set((Array.isArray(values) ? values : []).map(String))];

  const parseRegistry = () => {
    const node = document.getElementById(REGISTRY_ID);
    if (!node) return null;

    try {
      const source = JSON.parse(node.textContent);
      if (
        String(source.productId) !== '6827274797136' ||
        source.handle !== 'colorful-sneakers-6' ||
        normalize(source.optionName) !== 'color'
      ) {
        return null;
      }

      const white = uniqueStrings(source.groups?.White);
      const black = uniqueStrings(source.groups?.Black);
      const excluded = uniqueStrings(source.excluded);
      const allIds = [...white, ...black, ...excluded];

      if (!white.length || !black.length) return null;
      if (new Set(allIds).size !== allIds.length) return null;
      if (new Set(allIds).size !== 6) return null;

      return {
        ...source,
        groups: { White: white, Black: black },
        excluded,
        variantColors: source.variantColors || {}
      };
    } catch (error) {
      console.error('BPC color gallery registry could not be parsed.', error);
      return null;
    }
  };

  const registry = parseRegistry();
  if (!registry) return;

  const groupByMediaId = new Map();
  Object.entries(registry.groups).forEach(([color, mediaIds]) => {
    mediaIds.forEach((mediaId) => groupByMediaId.set(String(mediaId), color));
  });

  const excludedMediaIds = new Set(registry.excluded);
  const registeredMediaIds = new Set([
    ...registry.groups.White,
    ...registry.groups.Black,
    ...registry.excluded
  ]);

  const getMediaId = (element) => {
    const raw = element?.dataset?.target || element?.dataset?.mediaId || '';
    const match = String(raw).match(/(\d+)$/);
    return match ? match[1] : '';
  };

  const getMainSlides = () =>
    Array.from(document.querySelectorAll('media-gallery li.product__media-item[data-media-id]'))
      .filter((element) => registeredMediaIds.has(getMediaId(element)));

  const getThumbnails = () =>
    Array.from(document.querySelectorAll('media-gallery li.thumbnail-list__item[data-target]'))
      .filter((element) => registeredMediaIds.has(getMediaId(element)));

  const getModalItems = () =>
    Array.from(document.querySelectorAll('product-modal .product-media-modal__content > [data-media-id]'))
      .filter((element) => registeredMediaIds.has(getMediaId(element)));

  const focusableSelector =
    'a[href], button, input, select, textarea, summary, [tabindex], [contenteditable="true"]';

  const saveAndDisableFocus = (element) => {
    const focusables = [];
    if (element.matches?.(focusableSelector)) focusables.push(element);
    element.querySelectorAll?.(focusableSelector).forEach((node) => focusables.push(node));

    focusables.forEach((node) => {
      if (!node.hasAttribute('data-bpc-color-gallery-tabindex')) {
        node.setAttribute(
          'data-bpc-color-gallery-tabindex',
          node.hasAttribute('tabindex') ? node.getAttribute('tabindex') : '__none__'
        );
      }
      node.setAttribute('tabindex', '-1');
    });
  };

  const restoreFocus = (element) => {
    const focusables = [];
    if (element.matches?.(focusableSelector)) focusables.push(element);
    element.querySelectorAll?.('[data-bpc-color-gallery-tabindex]').forEach((node) => focusables.push(node));

    focusables.forEach((node) => {
      const previous = node.getAttribute('data-bpc-color-gallery-tabindex');
      if (previous === null) return;
      if (previous === '__none__') node.removeAttribute('tabindex');
      else node.setAttribute('tabindex', previous);
      node.removeAttribute('data-bpc-color-gallery-tabindex');
    });
  };

  const prepareElement = (element) => {
    const mediaId = getMediaId(element);
    if (!registeredMediaIds.has(mediaId)) return;

    const color = groupByMediaId.get(mediaId) || 'Excluded';
    const excluded = excludedMediaIds.has(mediaId);
    element.dataset.bpcMediaId = mediaId;
    element.dataset.bpcColorGroup = color;
    element.dataset.bpcGalleryEligible = excluded ? 'false' : 'true';

    if (!excluded) {
      element.classList.remove('product__media-item--variant', 'thumbnail-list_item--variant');
      element.hidden = false;
      element.removeAttribute('aria-hidden');
      element.removeAttribute('data-bpc-color-gallery-hidden');
      restoreFocus(element);
      return;
    }

    if (element.contains(document.activeElement)) document.activeElement.blur();
    element.hidden = true;
    element.setAttribute('aria-hidden', 'true');
    element.setAttribute('data-bpc-color-gallery-hidden', 'true');
    saveAndDisableFocus(element);
    element.classList.remove('is-active', 'active');
    element.querySelectorAll?.('[aria-current]').forEach((node) => node.removeAttribute('aria-current'));
  };

  const findPicker = () => {
    const candidates = Array.from(document.querySelectorAll('variant-selects, variant-radios'));
    return (
      candidates.find((picker) => {
        const url = picker.dataset.url || '';
        return url === '/products/colorful-sneakers-6' || url.endsWith('/products/colorful-sneakers-6');
      }) || null
    );
  };

  const getPickerColor = (picker) => {
    if (!picker) return '';
    const desiredName = normalize(registry.optionName);

    const matchingSelect = Array.from(picker.querySelectorAll('select')).find((select) => {
      const match = String(select.name || '').match(/^options\[(.+)]$/);
      if (match && normalize(match[1]) === desiredName) return true;
      const label = picker.querySelector(`label[for="${CSS.escape(select.id)}"]`);
      return normalize(label?.textContent) === desiredName;
    });
    if (matchingSelect) return matchingSelect.value;

    const matchingFieldset = Array.from(picker.querySelectorAll('fieldset')).find(
      (fieldset) => normalize(fieldset.querySelector('legend')?.textContent) === desiredName
    );
    return matchingFieldset?.querySelector('input:checked')?.value || '';
  };

  const getUrlVariantColor = () => {
    const variantId = new URL(window.location.href).searchParams.get('variant');
    return variantId ? registry.variantColors[String(variantId)] || '' : '';
  };

  const canonicalColor = (value) => {
    const key = normalize(value);
    if (key === 'white') return 'White';
    if (key === 'black') return 'Black';
    return '';
  };

  const getRuntimeOrder = (color) => {
    const oppositeColor = color === 'White' ? 'Black' : 'White';
    return [...registry.groups[color], ...registry.groups[oppositeColor]];
  };

  const reorderElements = (elements, orderedMediaIds) => {
    const orderIndex = new Map(orderedMediaIds.map((mediaId, index) => [String(mediaId), index]));
    const elementsByParent = new Map();

    elements.forEach((element) => {
      if (!element.parentElement || !orderIndex.has(getMediaId(element))) return;
      if (!elementsByParent.has(element.parentElement)) elementsByParent.set(element.parentElement, []);
      elementsByParent.get(element.parentElement).push(element);
    });

    elementsByParent.forEach((siblings, parent) => {
      const ordered = siblings
        .slice()
        .sort((left, right) => orderIndex.get(getMediaId(left)) - orderIndex.get(getMediaId(right)));
      const marker = document.createComment('bpc-color-gallery-order');
      parent.insertBefore(marker, siblings[0]);
      const fragment = document.createDocumentFragment();
      ordered.forEach((element) => fragment.appendChild(element));
      parent.insertBefore(fragment, marker);
      marker.remove();
    });
  };

  const setAccessiblePositions = (orderedMediaIds) => {
    const total = orderedMediaIds.length;

    getMainSlides().forEach((slide) => {
      const mediaId = getMediaId(slide);
      const index = orderedMediaIds.indexOf(mediaId);
      if (index < 0) return;

      const mediaColor = groupByMediaId.get(mediaId) || 'Product';
      slide.setAttribute('aria-posinset', String(index + 1));
      slide.setAttribute('aria-setsize', String(total));
      const openerLabel = slide.querySelector('.product__media-toggle .visually-hidden');
      if (openerLabel) {
        openerLabel.textContent = `Open ${mediaColor} product image ${index + 1} of ${total}`;
      }
    });

    getThumbnails().forEach((thumbnail) => {
      const mediaId = getMediaId(thumbnail);
      const index = orderedMediaIds.indexOf(mediaId);
      if (index < 0) return;

      const mediaColor = groupByMediaId.get(mediaId) || 'Product';
      const button = thumbnail.querySelector('button');
      thumbnail.dataset.mediaPosition = String(index + 1);
      thumbnail.setAttribute('aria-posinset', String(index + 1));
      thumbnail.setAttribute('aria-setsize', String(total));
      if (button) {
        button.setAttribute('aria-label', `${mediaColor} product image ${index + 1} of ${total}`);
      }
    });
  };

  const refreshSlider = (sliderComponent) => {
    if (!sliderComponent) return;
    window.requestAnimationFrame(() => {
      if (typeof sliderComponent.resetPages === 'function') sliderComponent.resetPages();
    });
  };

  const updateGalleryCounter = (gallery, activeMediaId, orderedMediaIds) => {
    const viewer = gallery.querySelector('slider-component[id^="GalleryViewer-"]');
    if (!viewer) return;

    const current = viewer.querySelector('.slider-counter--current');
    const total = viewer.querySelector('.slider-counter--total');
    const index = orderedMediaIds.indexOf(String(activeMediaId));

    if (current) current.textContent = String(Math.max(index + 1, 1));
    if (total) total.textContent = String(orderedMediaIds.length);
  };

  const captureActiveMedia = () => {
    const result = new Map();
    document.querySelectorAll('media-gallery[id^="MediaGallery-"]').forEach((gallery) => {
      const activeSlide = gallery.querySelector('li.product__media-item.is-active[data-media-id]');
      const mediaId = getMediaId(activeSlide);
      if (mediaId) result.set(gallery.id, mediaId);
    });
    return result;
  };

  class ColorGalleryResolver {
    constructor() {
      this.picker = findPicker();
      this.currentColor = '';
      this.pendingActiveMedia = new Map();
      this.abortController = new AbortController();
    }

    initialize() {
      const initial =
        canonicalColor(getPickerColor(this.picker)) ||
        canonicalColor(getUrlVariantColor()) ||
        canonicalColor(registry.initialColor);

      if (!initial) return;
      document.getElementById(INITIAL_STYLE_ID)?.remove();
      this.applyColor(initial, { force: true, colorChanged: true });

      if (this.picker && this.picker.dataset.bpcColorGalleryBound !== 'true') {
        this.picker.dataset.bpcColorGalleryBound = 'true';

        this.picker.addEventListener(
          'change',
          () => {
            this.pendingActiveMedia = captureActiveMedia();
          },
          { capture: true, signal: this.abortController.signal }
        );

        this.picker.addEventListener(
          'change',
          () => {
            window.requestAnimationFrame(() => {
              const selected = canonicalColor(getPickerColor(this.picker));
              if (!selected) return;
              const sameColor = selected === this.currentColor;
              this.applyColor(selected, {
                force: true,
                colorChanged: !sameColor,
                preferredMediaByGallery: sameColor ? this.pendingActiveMedia : null
              });
              this.pendingActiveMedia = new Map();
            });
          },
          { signal: this.abortController.signal }
        );
      }

      window.addEventListener(
        'popstate',
        () => {
          const selected = canonicalColor(getUrlVariantColor()) || canonicalColor(getPickerColor(this.picker));
          if (selected) this.applyColor(selected, { force: true, colorChanged: true });
        },
        { signal: this.abortController.signal }
      );

      window.addEventListener(
        'pageshow',
        () => {
          const selected = canonicalColor(getUrlVariantColor()) || canonicalColor(getPickerColor(this.picker));
          if (selected) this.applyColor(selected, { force: true, colorChanged: true });
        },
        { signal: this.abortController.signal }
      );
    }

    applyColor(
      color,
      { force = false, colorChanged = false, preferredMediaByGallery = null } = {}
    ) {
      const normalizedColor = canonicalColor(color);
      if (!normalizedColor) return;
      if (!force && normalizedColor === this.currentColor) return;

      const activeElement = document.activeElement;
      const activePickerControl = activeElement && this.picker?.contains(activeElement) ? activeElement : null;
      const orderedMediaIds = getRuntimeOrder(normalizedColor);
      const orderedMediaSet = new Set(orderedMediaIds);

      document.documentElement.setAttribute('data-bpc-color-gallery-color', normalize(normalizedColor));

      const slides = getMainSlides();
      const thumbnails = getThumbnails();
      const modalItems = getModalItems();

      [...slides, ...thumbnails, ...modalItems].forEach(prepareElement);
      reorderElements(slides, orderedMediaIds);
      reorderElements(thumbnails, orderedMediaIds);
      reorderElements(modalItems, orderedMediaIds);
      setAccessiblePositions(orderedMediaIds);

      document.querySelectorAll('media-gallery[id^="MediaGallery-"]').forEach((gallery) => {
        const activeSlide = gallery.querySelector('li.product__media-item.is-active[data-media-id]');
        const activeId = getMediaId(activeSlide);
        const preferredId = preferredMediaByGallery?.get(gallery.id) || '';
        const anchorId = registry.groups[normalizedColor][0];
        const targetId = colorChanged
          ? anchorId
          : orderedMediaSet.has(preferredId)
            ? preferredId
            : orderedMediaSet.has(activeId)
              ? activeId
              : anchorId;
        const targetSlide = Array.from(gallery.querySelectorAll('li.product__media-item[data-media-id]'))
          .find((slide) => getMediaId(slide) === targetId);

        if (targetSlide && typeof gallery.setActiveMedia === 'function') {
          gallery.setActiveMedia(targetSlide.dataset.mediaId, false);
        }

        const viewer = gallery.querySelector('slider-component[id^="GalleryViewer-"]');
        const thumbnailSlider = gallery.querySelector('slider-component[id^="GalleryThumbnails-"]');
        refreshSlider(viewer);
        refreshSlider(thumbnailSlider);

        window.requestAnimationFrame(() => updateGalleryCounter(gallery, targetId, orderedMediaIds));
      });

      this.currentColor = normalizedColor;
      document.documentElement.setAttribute('data-bpc-color-gallery-ready', 'true');
      document.documentElement.setAttribute('data-bpc-color-gallery-mode', 'selected-color-first');

      if (activePickerControl && document.activeElement !== activePickerControl) {
        activePickerControl.focus({ preventScroll: true });
      }
    }
  }

  const start = () => {
    const resolver = new ColorGalleryResolver();
    resolver.initialize();
    window.BpcColorGalleryResolverV1 = resolver;
  };

  Promise.all([
    customElements.whenDefined('variant-selects'),
    customElements.whenDefined('media-gallery')
  ]).then(start);
})();
